import { FileExtension, MAX_UPLOAD_FILE_SIZE_MB } from "@inrev/common";
import type { ErrorMessage } from "./error";

export const UploadErrorCode = {
	fileTooLarge: "file-too-large",
	fileInvalidType: "file-invalid-type",
	fileIsEncrypted: "file-is-encrypted",
	fileTooSmall: "file-too-small",
	tooManyFiles: "too-many-files",
	network: "network",
	server: "server",
	unknown: "unknown",
} as const;

export type UploadErrorCode = (typeof UploadErrorCode)[keyof typeof UploadErrorCode];

const UPLOAD_ERROR_CODES = new Set<string>(Object.values(UploadErrorCode));

export const isUploadErrorCode = (code: string): code is UploadErrorCode =>
	UPLOAD_ERROR_CODES.has(code);

type UploadErrorMessageContext = {
	allowedExtensions?: FileExtension[];
};

const formattedExtensions = (allowedExtensions: FileExtension[]) =>
	allowedExtensions.map((ext) => `.${ext}`).join(", ");

export const getUploadErrorMessage = (
	code: UploadErrorCode,
	ctx: UploadErrorMessageContext = {},
): ErrorMessage => {
	switch (code) {
		case UploadErrorCode.fileTooLarge:
			return {
				header: "File too large",
				description: `Please upload a file that is smaller than ${MAX_UPLOAD_FILE_SIZE_MB}MB.`,
				severity: "warn",
			};
		case UploadErrorCode.fileInvalidType:
			return {
				header: "Invalid file type",
				description: ctx.allowedExtensions
					? `Please upload a file that is one of the following types: ${formattedExtensions(ctx.allowedExtensions)}`
					: "That file type isn't supported.",
				severity: "warn",
			};
		case UploadErrorCode.fileIsEncrypted:
			return {
				header: "Encrypted file",
				description:
					"This file is encrypted or password-protected and can't be processed. Please upload an unlocked copy.",
				severity: "warn",
			};
		case UploadErrorCode.fileTooSmall:
			return {
				header: "Empty file",
				description:
					"This file is empty. If uploading from a zip folder, please unzip the folder and try again.",
				severity: "warn",
			};
		case UploadErrorCode.tooManyFiles:
			return {
				header: "Too many files",
				description: "Please upload one file at a time.",
				severity: "warn",
			};
		case UploadErrorCode.network:
			return {
				header: "Connection problem",
				description: "We couldn't reach the server. Check your connection and try again.",
				severity: "warn",
			};
		case UploadErrorCode.server:
		case UploadErrorCode.unknown:
			return {
				header: "Upload failed",
				description: "Something went wrong while uploading your file. Please try again.",
				severity: "error",
			};
	}
};

export const getUploadErrorInlineMessage = (
	code: UploadErrorCode,
	ctx: UploadErrorMessageContext = {},
): string => getUploadErrorMessage(code, ctx).description;

export const isNetworkError = (err: Error): boolean =>
	err.message?.includes("Failed to fetch") ?? false;
